import { useCallback, useEffect, useMemo, useState } from "react";
import { ZoomIn, X, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "../components/Reveal";
import LazyImg from "../components/LazyImg";
import { PageHero } from "../components/Shared";
import { fetchGallerySupabase, galleryImageUrlSupabase } from "../lib/supabase";
import SEO from "../components/SEO";

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetchGallerySupabase()
      .then(setImages)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const seen = [];
    images.forEach((im) => { if (!seen.includes(im.category)) seen.push(im.category); });
    return seen;
  }, [images]);

  const photos = useMemo(
    () => (filter === "all" ? images : images.filter((p) => p.category === filter)),
    [filter, images]
  );

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setLightbox((i) => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lightbox, close, prev, next]);

  return (
    <>
      <SEO
        title="Gallery | SUV Engineers"
        description="View high-quality, on-site photographs showing our electrical installations, wiring, and network infrastructure implementations."
      />
      <PageHero crumb="Gallery" title="On-site photos from our projects.">
        A look at real electrical installation and infrastructure work delivered by SUV Engineers
        across industrial, commercial and campus projects.
      </PageHero>

      <section className="section">
        <div className="section-inner">
          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : images.length === 0 ? (
            <div className="empty-state">No photos yet.</div>
          ) : (
            <>
              <div className="gallery-filter" data-testid="gallery-filter">
                <button className={`tab ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")} data-testid="gallery-filter-all">
                  All Photos<span className="count">{images.length}</span>
                </button>
                {categories.map((c) => (
                  <button key={c} className={`tab ${filter === c ? "active" : ""}`} onClick={() => setFilter(c)}>
                    {c}<span className="count">{images.filter((im) => im.category === c).length}</span>
                  </button>
                ))}
              </div>

              <div className="photo-grid" data-testid="photo-grid">
                {photos.map((p, i) => (
                  <Reveal key={p.id} className="photo-card" delay={(i % 8) * 40}>
                    <button
                      type="button"
                      style={{ all: "unset", display: "block", width: "100%", cursor: "pointer" }}
                      onClick={() => setLightbox(i)}
                      data-testid={`photo-${i}`}
                      aria-label={`View ${p.category} photo`}
                    >
                      <span className="zoom-ic"><ZoomIn /></span>
                      <LazyImg src={galleryImageUrlSupabase(p.image_url)} alt={p.caption ? `${p.caption} — ${p.category} | SUV Engineers` : `${p.category} — SUV Engineers`} />
                      <span className="overlay"><span className="cap">{p.caption || p.category}</span></span>
                    </button>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {lightbox !== null && photos[lightbox] && (
        <div className="lightbox" onClick={close} data-testid="lightbox">
          <div className="lb-count">{lightbox + 1} / {photos.length}</div>
          <button className="lb-btn lb-close" onClick={close} data-testid="lightbox-close" aria-label="Close"><X /></button>
          <button className="lb-btn lb-prev" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous"><ChevronLeft /></button>
          <img className="lb-img" src={galleryImageUrlSupabase(photos[lightbox].image_url)} alt={photos[lightbox].category} onClick={(e) => e.stopPropagation()} />
          <button className="lb-btn lb-next" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next"><ChevronRight /></button>
          <div className="lb-cap">{photos[lightbox].caption || photos[lightbox].category}</div>
        </div>
      )}
    </>
  );
}
